/**
 * The club cover (design_handoff_club_pages/README.md §Cover): the event cover's geometry — the
 * same `EventCover` the event and Pro pages draw — with the club identity in the text column and
 * the cover video riding photo 1's tile.
 *
 *   desktop  radius 28, 500px (cover ≥ 1100 wide) else 460; text column 36/40 with the 132px logo,
 *            header pill + CLUB · TOWN, name, italic tagline, the trust line with member faces,
 *            RUN BY at the foot (one link to the owner's Resume).
 *   phone    photos only, 220px radius 22; the page renders the identity card below it.
 *
 * `editing` turns every tile into a drag target and shows the poster instead of the video.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import EventCover from '../event/EventCover';
import ProCoverVideo from '../pro/ProCoverVideo';
import TintSquare from '../hand/TintSquare';
import { RING_ON } from '../surface/SurfaceStyleContext';

const MUTED = '#6B7280';
const FAINT = '#9CA3AF';
const heightFor = (W, layout, mobile) => (mobile ? 220 : W >= 1100 ? 500 : 460);
const label = (size, spacing = '0.16em') => ({ fontSize: size, fontWeight: 700, letterSpacing: spacing, textTransform: 'uppercase' });

/** Header pill colours per README §Pills (status / role): OWNER cyan · ADMIN/MEMBER black · PUBLIC lime · PRIVATE hairline · MEMBERS ONLY blush · INVITED cyan. */
export const HEADER_PILL = {
  cyan: ['#00FFFF', '#000'], black: ['#000', '#fff'], lime: ['#D9F99D', '#000'], hairline: ['#E5E7EB', '#000'], blush: ['#FFCCE6', '#000'],
  outline: ['#F2F2F2', '#000'], ghost: ['#F2F2F2', '#000'],
};

export function ClubLogo({ club, size, radius, fontSize }) {
  return (
    <span className="flex flex-none items-center justify-center overflow-hidden" style={{ width: size, height: size, borderRadius: radius, background: '#fff', boxShadow: 'inset 0 0 0 2px #F2F2F2' }} data-testid="club-logo">
      {club.avatarUrl
        ? <img src={club.avatarUrl} alt="" className="block h-full w-full object-contain" referrerPolicy="no-referrer" />
        : <TintSquare name={club.name} size={size} fontSize={fontSize} tintKey={club.id} style={{ borderRadius: radius }} />}
    </span>
  );
}

export function ClubPills({ club, state, small = false }) {
  const [bg, fg] = HEADER_PILL[state?.pill?.tone] || HEADER_PILL.black;
  const kind = `CLUB${club.locationCity ? ` · ${String(club.locationCity).split(',')[0]}` : club.isGlobal ? ' · GLOBAL' : ''}`;
  return (
    <span className="flex flex-wrap items-center" style={{ gap: small ? 8 : 10 }}>
      <span className="rounded-full" style={{ padding: small ? '4px 10px' : '5px 12px', background: bg, color: fg, fontSize: small ? 9 : 10, fontWeight: 800, letterSpacing: small ? '0.14em' : '0.16em' }} data-testid="club-pill">{state?.pill?.label || 'CLUB'}</span>
      <span style={{ ...label(small ? 9 : 11, small ? '0.16em' : '0.18em'), color: MUTED }}>{kind}</span>
    </span>
  );
}

export function TrustLine({ state, size = 12 }) {
  if (!state?.trust) return null;
  return <span className="uppercase" style={{ fontSize: size, fontWeight: 700, letterSpacing: '0.06em', color: state.trust.color || '#000' }} data-testid="club-trust">{state.trust.label}</span>;
}

/** Overlapping member faces + a black +N pill → opens MEMBERS; then `N MEMBERS · SINCE MAY 2026`. */
export function MemberFaces({ members = [], total = 0, since, onOpen, size = 34 }) {
  const faces = members.filter((m) => m.avatarUrl).slice(0, 3);
  const more = Math.max(0, total - faces.length);
  return (
    <span className="flex flex-wrap items-center" style={{ gap: '10px 14px' }}>
      <button type="button" onClick={onOpen} aria-label="Members" className="flex items-center" style={{ padding: '0 0 0 10px', border: 'none', background: 'none', cursor: onOpen ? 'pointer' : 'default' }} data-testid="member-faces">
        {faces.map((m) => <img key={m.userId} src={m.avatarUrl} alt="" referrerPolicy="no-referrer" style={{ width: size, height: size, marginLeft: -10, borderRadius: '50%', border: '3px solid #fff', objectFit: 'cover', boxSizing: 'border-box', display: 'block' }} />)}
        <span style={{ height: size, marginLeft: -10, padding: '0 10px', borderRadius: 999, border: '3px solid #fff', boxSizing: 'border-box', background: '#000', color: '#fff', display: 'flex', alignItems: 'center', fontSize: 10, fontWeight: 700 }}>+{more}</span>
      </button>
      <span style={{ fontSize: 12, letterSpacing: '0.06em', color: MUTED, textTransform: 'uppercase' }}>{total} {total === 1 ? 'MEMBER' : 'MEMBERS'}{since ? ` · SINCE ${since}` : ''}</span>
    </span>
  );
}

/** RUN BY — one link to the owner's Resume. */
export function RunBy({ club, compact = false }) {
  const navigate = useNavigate();
  if (!club.ownerHandle && !club.ownerName) return null;
  const go = () => club.ownerHandle && navigate(`/${club.ownerHandle}`);
  const face = (
    <span className="flex flex-none items-center justify-center overflow-hidden rounded-full" style={{ width: compact ? 36 : 48, height: compact ? 36 : 48, background: '#E5E7EB', fontWeight: 700 }}>
      {club.ownerAvatarUrl ? <img src={club.ownerAvatarUrl} alt="" className="block h-full w-full object-cover" referrerPolicy="no-referrer" /> : (club.ownerName || '?').charAt(0).toUpperCase()}
    </span>
  );
  return (
    <button type="button" onClick={go} className="flex items-center text-left font-mono transition-colors hover:bg-[#F5F5F5]" style={{ gap: compact ? 10 : 12, padding: compact ? '6px 14px 6px 6px' : '6px 18px 6px 6px', borderRadius: 999, border: 'none', background: compact ? '#F5F5F5' : 'none', cursor: 'pointer', color: 'inherit', alignSelf: 'flex-start', marginTop: compact ? 0 : 'auto' }} data-testid="run-by">
      {face}
      <span className="flex min-w-0 flex-col" style={{ gap: 3 }}>
        <span style={{ ...label(compact ? 8 : 9), color: MUTED }}>RUN BY</span>
        <span className="truncate" style={{ fontSize: compact ? 12 : 14, fontWeight: 700 }}>{club.ownerName || `@${club.ownerHandle}`} {club.ownerHandle && <span style={{ fontWeight: 400, color: MUTED }}>@{club.ownerHandle}</span>}</span>
      </span>
    </button>
  );
}

function EditButton({ open, onClick, phone }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={open} data-testid="edit-cover"
      className="absolute flex items-center rounded-full font-mono uppercase"
      style={{ right: phone ? 10 : 16, top: phone ? 10 : 16, zIndex: 3, gap: 8, height: phone ? 36 : 40, padding: phone ? '0 14px' : '0 16px', border: '2px solid transparent', boxSizing: 'border-box', background: open ? RING_ON : '#fff', color: open ? '#fff' : '#000', fontSize: phone ? 9 : 10, fontWeight: 800, letterSpacing: '0.14em', cursor: 'pointer' }}>
      {!phone && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M3 5h18v14H3zM3 15l5-5 4 4 3-3 6 6" /></svg>}
      {open ? 'EDITING COVER' : 'EDIT COVER'}
    </button>
  );
}

export default function ClubCover({ club, state, members = [], memberCount, since, photos = [], layout, split, video, phone = false, admin = false, editing = false, onToggleEdit, onFocal, onOpenPhoto, onRatio, onOpenMembers }) {
  const hasPhotos = photos.length > 0;
  const renderMedia = (photo, i, pos) => {
    if (i !== 0 || !video || editing) return null;
    return <ProCoverVideo photoUrl={photo.src} video={video} objectPosition={pos} />;
  };
  const common = { photos, layout, split, editable: editing, onFocal, onOpenPhoto: editing ? undefined : onOpenPhoto, onRatio, renderMedia, heightFor, testId: 'club-cover' };
  const total = memberCount ?? club.memberCount ?? members.length;

  if (phone) {
    return (
      <div className="relative overflow-hidden bg-white" style={{ borderRadius: 22, height: 220 }} data-testid="club-cover-mobile" data-layout={hasPhotos ? undefined : 'none'}>
        {hasPhotos
          ? <EventCover {...common} mobile />
          : <div className="flex h-full items-center justify-center" style={{ background: '#F2F2F2', ...label(10), color: FAINT }}>NO PHOTOS YET</div>}
        {admin && <EditButton open={editing} onClick={onToggleEdit} phone />}
      </div>
    );
  }

  return (
    <div className="relative" data-testid="club-header">
      <EventCover {...common} className="rounded-[28px]" textPadding="36px 40px">
        {({ textPx }) => {
          const room = textPx - 154;
          const titleFs = room > 520 ? 46 : room > 360 ? 38 : 30;
          return (
            <>
              <div className="flex items-center" style={{ gap: 22 }}>
                <ClubLogo club={club} size={132} radius={24} fontSize={56} />
                <span className="flex min-w-0 flex-col" style={{ gap: 12 }}>
                  <ClubPills club={club} state={state} />
                  <h1 className="m-0 font-mono" style={{ fontSize: titleFs, fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.01em', textWrap: 'pretty', overflowWrap: 'anywhere', textTransform: 'none' }} data-testid="club-name">{club.name}</h1>
                </span>
              </div>
              {club.tagline && <p style={{ margin: 0, fontFamily: '"Times New Roman", Times, serif', fontStyle: 'italic', fontSize: 24, lineHeight: 1.35, maxWidth: 560, textWrap: 'pretty' }} data-testid="club-tagline">{club.tagline}</p>}
              <span className="flex flex-wrap items-center" style={{ gap: '10px 14px' }}>
                <MemberFaces members={members} total={total} since={since} onOpen={onOpenMembers} />
                <TrustLine state={state} />
              </span>
              <RunBy club={club} />
            </>
          );
        }}
      </EventCover>
      {!hasPhotos && (
        <div className="absolute flex flex-col items-center justify-center text-center" style={{ right: 32, top: 32, bottom: 32, width: '34%', borderRadius: 22, background: '#F5F5F5', gap: 8, padding: 24, boxSizing: 'border-box' }} data-testid="no-photos-strip">
          <span style={{ ...label(12), color: FAINT }}>NO PHOTOS</span>
          <span style={{ fontSize: 12, lineHeight: 1.6, color: MUTED }}>{admin ? 'Add photos with EDIT COVER. Pick a layout and drag each one into frame.' : 'The club hasn’t added cover photos yet.'}</span>
        </div>
      )}
      {admin && <EditButton open={editing} onClick={onToggleEdit} />}
    </div>
  );
}
